import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";

export default function ProjectsPage() {
  const projects = [
    {
      name: "Sì Pattina – Live Group Ride Tracking",
      folder: "SiPattina",
      count: 5,
      live: "https://si-pattini.web.app/welcome",
      description:
        "A live GPS platform for city skating groups. One phone records the route while everyone who joined follows the line drawing itself on the map in real time — then the ride becomes a shareable card, sticker, video or GPX file for Insta360, GoPro and Garmin.",
      tech: [
        "React",
        "Firebase",
        "Live GPS Tracking",
        "Interactive Maps",
        "GPX Export",
        "On-Device Rendering",
        "Capacitor (Android)",
        "Italian UI"
      ],
    },
    {
      name: "ManB2B – Field Team Management",
      folder: "Management",
      count: 4,
      live: "https://manp2p.web.app",
      description:
        "One place for every job, every hour and every person. Recurring schedules, start/stop hour tracking with flagged missing check-outs, photo reports, separate manager, supervisor and worker views, and an automatic monthly PDF invoice. Four languages with full right-to-left support.",
      tech: [
        "React",
        "Firebase Authentication",
        "Firestore",
        "Role-Based Access Control",
        "Recurring Schedules",
        "Time Tracking",
        "PDF Invoicing",
        "i18n + Full RTL"
      ],
    },
   {
  name: "PocketLinks – Smart Link Manager App",
  folder: "PocketLinks",
  playStore: "https://play.google.com/store/apps/details?id=com.auryxstudio.pocketlinks",
  count: 4,
  description:
    "A simple and efficient mobile app that lets users save, organize, and manage important links from different apps in one place using the Android share system.",
  tech: [
    "Flutter",
    "Dart",
    "SQLite (Local Database)",
    "Android Share Intent",
    "Offline Storage",
    "Clean UI Design"
  ],
    },
   {
  name: "Nmoney – Smart Money Manager App",
  folder: "nmoney",
  playStore: "https://play.google.com/store/apps/details?id=com.auryxstudio.nmoney",
  count: 4,
  description:
    "A smart and easy-to-use finance management app built with Flutter that helps users track income, expenses, and jobs in one place. Nmoney allows users to manage multiple job types, calculate earnings, and monitor daily financial activity with a clean and intuitive interface.",
  tech: [
    "Flutter",
    "Dart",
    "Firebase",
    "Local Data Storage",
    "State Management",
    "Clean UI Architecture"
  ],
   },

    {
      name: "Church Community App",
      folder: "church",
      count: 4,
      description:
        "A complete community app for church members with events, media, and announcements.",
      tech: ["Flutter", "Firebase", "Authentication", "Cloud Storage"],
    },
    {
      name: "TozBest – Task Manager",
      folder: "tozbest",
      count: 3,
      description:
        "Collaborative task manager app to track progress and share goals with friends.",
      tech: ["Flutter", "Firebase Realtime DB", "Notifications"],
    },
    {
      name: "Modern 3D Concept Design",
      folder: "modern3d",
      count: 8,
      description:
        "3D animated app design concept featuring chat, e-shop, and profile screens.",
      tech: ["Flutter", "Rive Animations", "UI Design"],
    },
    {
  name: "Caffezo – Multi-Tenant Restaurant SaaS",
  folder: "Caffezo",
  count: 8,
  live: "https://caffezo.com",
  description:
    "A commercial SaaS platform for restaurants and bars across Europe, sold on three subscription tiers. Each venue builds its own branded page and QR menu, takes orders from the table, and gets a dashboard for order totals, staff shifts, automatic payroll and table bookings — live in 8 languages.",
  tech: [
    "React",
    "Firebase",
    "Multi-Tenant Architecture",
    "Subscription Tiers",
    "QR Menu System",
    "8 Languages",
    "Shift & Payroll Automation",
    "Table Booking"
  ],
},
  ];

  return (
    <div className="min-h-screen bg-[#050816] text-white px-6 py-10">
      {/* Back Button */}
      <Link
        to="/"
        className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition mb-10"
      >
        <ArrowLeft size={18} /> Back to Portfolio
      </Link>

      <motion.h1
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-cyan-400 via-sky-400 to-fuchsia-400 bg-clip-text text-transparent"
      >
        My Projects
      </motion.h1>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-6xl mx-auto">
        {projects.map((project, i) => (
  <Link key={i} to={`/project/${project.folder}`}>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.2 }}
      className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-md shadow-[0_0_25px_-8px_rgba(56,189,248,0.3)] hover:shadow-cyan-400/20 transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <h2 className="text-xl font-semibold text-cyan-300">
          {project.name}
        </h2>
        {project.live && (
          <span
            role="link"
            tabIndex={0}
            title={`Open ${project.live}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.open(project.live, "_blank", "noopener,noreferrer");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                window.open(project.live, "_blank", "noopener,noreferrer");
              }
            }}
            className="flex-shrink-0 inline-flex items-center gap-1.5 mt-1 cursor-pointer
              text-[10px] font-semibold px-2 py-1 rounded-md
              bg-emerald-400/10 border border-emerald-400/30 text-emerald-300
              hover:bg-emerald-400/20 hover:border-emerald-400/50 transition-colors"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live
          </span>
        )}
        {project.playStore && (
          <span
            role="link"
            tabIndex={0}
            title="Get it on Google Play"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.open(project.playStore, "_blank", "noopener,noreferrer");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                window.open(project.playStore, "_blank", "noopener,noreferrer");
              }
            }}
            className="flex-shrink-0 inline-flex items-center gap-1.5 mt-1 cursor-pointer
              text-[10px] font-semibold px-2 py-1 rounded-md
              bg-emerald-400/10 border border-emerald-400/30 text-emerald-300
              hover:bg-emerald-400/20 hover:border-emerald-400/50 transition-colors"
          >
            <Smartphone size={10} />
            Google Play
          </span>
        )}
      </div>
      <p className="text-slate-400 text-sm mb-4">{project.description}</p>

      <div className="grid grid-cols-2 gap-2 mb-4">
        {[...Array(Math.min(project.count, 4))].map((_, index) => (
          <img
            key={index}
            src={require(`./assets/${project.folder}/${index + 1}.png`)}
            alt={`${project.name} ${index + 1}`}
            className="rounded-lg object-cover h-32 w-full hover:scale-105 transition-transform"
          />
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {project.tech.map((tech, idx) => (
          <span
            key={idx}
            className="text-xs border border-cyan-400/30 text-cyan-300 px-2 py-1 rounded-full bg-white/5"
          >
            {tech}
          </span>
        ))}
      </div>
    </motion.div>
  </Link>
))}

      </div>
    </div> 
  );
}
